import type { SettingValueType, SettingValue, SettingDefinition } from './types.js'
import {
	get,
	set,
	reset,
	isModified,
	getDefaultValue,
	getDefinition
} from './settings-service.svelte.js'

export type SettingHandle<T extends SettingValueType = SettingValueType> = {
	readonly value: SettingValue<T> | undefined
	readonly isModified: boolean
	readonly defaultValue: SettingValue<T> | undefined
	readonly definition: SettingDefinition | undefined
	set(newValue: SettingValue<T>): void
	reset(): void
}

function createHandle<T extends SettingValueType>(key: string): SettingHandle<T> {
	const value = $derived(get<T>(key))
	const modified = $derived(isModified(key))
	const defValue = $derived(getDefaultValue<T>(key))
	const def = $derived(getDefinition(key))

	return {
		get value() {
			return value
		},
		get isModified() {
			return modified
		},
		get defaultValue() {
			return defValue
		},
		get definition(): SettingDefinition | undefined {
			return def
		},
		set(newValue: SettingValue<T>) {
			set(key, newValue)
		},
		reset() {
			reset(key)
		}
	}
}

export function useSetting<T extends Record<string, string>>(
	keys: T
): { [K in keyof T]: SettingHandle } {
	const entries = Object.entries(keys).map(([name, key]) => [name, createHandle(key)] as const)
	return Object.fromEntries(entries) as { [K in keyof T]: SettingHandle }
}
